import { Component, ComponentClass } from 'react';
import {
  Condition,
  FilterTypes,
  NOW,
  StringFilterSubTypes,
  TimestampFilterSubTypes,
} from '../Filter';
import {
  convertYearOperandLabel,
  FilterOperators,
  timestampOperators,
} from '../FilterOperators';
import { compose, withProps } from 'react-recompose';
import {
  isNil,
  isEmpty,
  includes,
  nth,
  map,
  isArray,
  isFunction,
  isEqual,
  range,
  toString,
  find,
  last,
  concat,
  head,
  join,
  get,
  omit,
  isObject,
  pick,
  reject,
} from 'lodash';
import moment, { globalDateWithoutTimeFormat } from '../../../common/Moment';
import { parse } from 'papaparse';
import { messages } from '../../../i18n';
import {
  IOperator,
  FilterDialogTypes,
} from '../../../datasets/interfaces/filter.interface';
import { connect } from 'react-redux';
import {
  getDynamicFieldConfig,
  VIZ_SELECTORS,
} from '../../../common/redux/selectors/viz-selectors';
import { FiscalCalendarYear } from '../../../views/edit-dataset-settings/edit-dataset-settings.interfaces';
import { DynamicFieldConfig } from '../../../common';
import { FieldsDropDown } from '../FieldsDropDown';
import { ISearchableDropdownOption } from '../../../ui/dropdowns/searchable-dropdown';
import { makeDropdownOption } from '../../../ui/dropdowns/searchable-dropdown/searchable-dropdown.utils';
import {
  FormGroup,
  StyledFormControl,
  StyledSearchableDropdown,
} from './filter-condition.styles';
import {
  IFilterConditionProps,
  IFilterConditionState,
} from './filter-condition.interface';
import { DatePicker } from '../../../components/calendar/date-picker';
import { ZonedDateTime } from '@internationalized/date';
import {
  nowOrSpecifiedDate,
  toIso8601,
} from '../../../components/calendar/date-picker/date-picker.utils';
import { isMoment } from 'moment';
import { Viz } from '../../VizUtil';
import { IAnyAttribute } from '../../../datasets';
import { ChartSpecs } from '../../ChartSpecs';
import isMatch from 'lodash/isMatch';
import { dateFilterSubTypeOrDefault } from '../filter.utils';

class UnconnectedFilterCondition extends Component<
  IFilterConditionProps,
  IFilterConditionState
> {
  state: IFilterConditionState;
  constructor(props: IFilterConditionProps) {
    super(props);
    let operands =
      isArray(props.condition.operands) && props.condition.operands.length > 0
        ? props.condition.operands
        : this.getDefaultOperandArr(props.condition.operator, props.filterType);
    let operator = isNil(props.condition.operator)
      ? props.defaultSelected
      : props.condition.operator;
    if (operator === 'currentUser') {
      operator = 'dynamicField';
    }
    const { dynamicField } = props;
    const fieldSlug = dynamicField?.fieldSlug;
    if (operator === 'dynamicField' && fieldSlug) {
      operands = [fieldSlug];
    }
    const operandText = this.getOperandText(operands);
    this.state = {
      operator,
      operands,
      operandText,
    };
    this.onOperandChange = this.onOperandChange.bind(this);
  }

  getOperandText(operands) {
    return isEmpty(operands)
      ? ''
      : operands
          .filter(o => !isEmpty(o))
          .map(o => (includes(o, ',') ? `"${o}"` : o))
          .join(', ');
  }

  getDefaultOperandArr(operator, filterType) {
    if (!isNil(operator) && !this.props.operators[operator].requiresOperand) {
      return [];
    }
    switch (filterType) {
      case 'DATE':
        return [toIso8601(nowOrSpecifiedDate())];
      default:
        return [''];
    }
  }

  componentDidMount() {
    this._onChange(this.state.operator, this.state.operands);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !isEqual(this.state.operator, prevState.operator) ||
      !isEqual(this.state.operands, prevState.operands)
    ) {
      this._onChange(this.state.operator, this.state.operands);
    }

    if (
      !isEqual(
        prevProps?.condition?.operator,
        this.props?.condition?.operator,
      ) &&
      (!isEqual(this.state.operator, this.props.condition?.operator) ||
        !isEqual(this.state.operands, this.props.condition?.operands))
    ) {
      let operator = isNil(this.props.condition?.operator)
        ? this.props.defaultSelected
        : this.props.condition?.operator;
      if (operator === 'currentUser') {
        operator = FilterOperators.forFilterType(FilterTypes.STRING)
          .dynamicField.key;
      }
      let operands =
        isArray(this.props.condition?.operands) &&
        this.props.condition?.operands.length > 0
          ? this.props.condition?.operands
          : [];
      if (
        operator ===
          FilterOperators.forFilterType(FilterTypes.STRING).dynamicField.key &&
        this.props.dynamicField?.fieldSlug
      ) {
        operands = [this.props.dynamicField?.fieldSlug];
      }
      const newState = {
        operator,
        operands,
      };
      this.setState(newState);
    }
  }

  onInTermOperandChange(operandIdx, changingValue) {
    const operand = nth(this.state.operands, operandIdx);
    if (isNil(operand)) {
      return;
    }
    this.setState({
      operands: map(this.state.operands, (_operandValue, _operandIdx) => {
        if (_operandIdx === operandIdx) {
          return changingValue;
        }
        return _operandValue;
      }),
    });
  }

  onOperandChange(eventOrISOString) {
    const { filterType, filterSubType } = this.props;

    let value = eventOrISOString;
    if (isObject(eventOrISOString)) {
      value = (eventOrISOString as any)?.currentTarget?.value;
    }

    if (
      filterType === FilterTypes.STRING &&
      filterSubType === StringFilterSubTypes.SET_CONDITION
    ) {
      this.setState({ operandText: value });
      // support multiple values via comma separated list
      this.parseOperandValues(value);
    } else {
      const newOperands = [value];
      this.setState({ operands: newOperands });
    }
  }

  onFieldOperandChange(event) {
    this.setState({
      operands: [event.name],
    });
  }

  onFieldOperandValueChange(event) {
    this.setState({
      operands: [this.state.operands[0], event.currentTarget.value],
    });
  }

  async parseOperandValues(value) {
    try {
      const parsedValue = await this._parseOperandValues(value);

      if (value !== this.state.operandText) {
        // this is a result parsed from text that isn't there anymore, ignore it
        return;
      }

      if (!isEmpty(parsedValue) && isArray(parsedValue)) {
        this.setState({
          operands: parsedValue,
          operandText: value,
          parseError: undefined,
        });
      } else if (isEmpty(parsedValue)) {
        this.setState({ operands: [], operandText: '', parseError: undefined });
      }
    } catch (e) {
      this.setState({ operandText: value, parseError: e, operands: [] });
    }
  }

  _parseOperandValues(operandString) {
    if (isEmpty(operandString)) {
      return new Promise(resolve => resolve([]));
    } else {
      return new Promise((resolve, reject) => {
        parse(operandString, {
          delimiter: ',',
          quoteChar: '"',
          complete: ({ errors, data }) => {
            if (isEmpty(errors) && !isEmpty(data)) {
              const newOperands = data[0];
              resolve(newOperands);
            } else {
              reject(errors);
            }
          },
        });
      });
    }
  }

  getOperandValue() {
    const { operands } = this.state;
    const { filterType, filterSubType } = this.props;
    if (
      filterType === FilterTypes.STRING &&
      filterSubType === StringFilterSubTypes.SET_CONDITION
    ) {
      return this.state.operandText;
    } else {
      return !isNil(operands) && operands.length > 0 ? operands[0] : '';
    }
  }

  getFieldOperandValue() {
    const { operands } = this.state;
    return operands?.length > 1 ? operands[1] : '';
  }

  _onChange(operatorKey: string, operands) {
    const operator = this.props.operators[operatorKey];
    if (isFunction(this.props.onChange)) {
      this.props.onChange(
        this.createConditionsWithDefaults(operator, operands),
      );
    }
  }

  createOperandMenuItems(
    startIdx: number,
    endIdx: number,
    operandValue: string,
    selectIdx: number,
  ): {
    menuOptions: ISearchableDropdownOption;
    defaultValue?: ISearchableDropdownOption;
    title: string;
    onSelect: (_value: string) => void;
  } {
    return {
      menuOptions: map(range(startIdx, endIdx), _idx =>
        makeDropdownOption(`${_idx}`),
      ),
      defaultValue: makeDropdownOption(operandValue),
      title: toString(operandValue),
      onSelect: _operandValue =>
        this.onInTermOperandChange(selectIdx, _operandValue),
    };
  }

  createConditionsWithDefaults(
    operator: IOperator,
    operands: string[],
  ): Condition {
    let cleanOperands = [];
    if (
      operator.requiresFieldPicker &&
      operator.requiresOperand &&
      !isEmpty(operands)
    ) {
      cleanOperands = operands.length < 2 ? [...operands, ''] : operands;
    } else if (operator.requiresOperand) {
      cleanOperands = operands;
    }
    return new Condition(operator.key, cleanOperands);
  }

  isInTermOperator(operatorKey) {
    return find(
      [
        timestampOperators.inWeek,
        timestampOperators.inMonth,
        timestampOperators.inQuarter,
        timestampOperators.weekInQuarter,
        timestampOperators.inYear,
      ],
      { key: operatorKey },
    );
  }

  shouldUpdateOperands(
    prevOperatorKey: string,
    nextOperatorKey: string,
  ): boolean {
    const dynamicFieldKey = FilterOperators.forFilterType(FilterTypes.STRING)
      .dynamicField.key;
    return (
      !isEmpty(this.isInTermOperator(prevOperatorKey)) ||
      !isEmpty(this.isInTermOperator(nextOperatorKey)) ||
      isEqual(prevOperatorKey, dynamicFieldKey) ||
      isEqual(nextOperatorKey, dynamicFieldKey)
    );
  }
  onOperatorChange(nextOperatorKey: string) {
    const prevOperatorKey = this.state.operator;
    if (this.shouldUpdateOperands(prevOperatorKey, nextOperatorKey)) {
      const operands = this.getOperandDefaults(nextOperatorKey);
      const operandText = this.getOperandText(operands);
      this.setState({
        operator: nextOperatorKey,
        operands,
        operandText,
      });
    } else {
      this.setState({
        operator: nextOperatorKey,
      });
    }
  }

  getOperandDefaults(operatorKey: string): any[] {
    switch (this.props.filterType) {
      case 'DATE': {
        const inTermOperator = this.isInTermOperator(operatorKey);

        if (inTermOperator) {
          switch (operatorKey) {
            case timestampOperators.inWeek.key: {
              return ['0', 'ALL'];
            }
            case timestampOperators.inMonth.key: {
              return ['1', 'ALL'];
            }
            case timestampOperators.inQuarter.key: {
              return ['1', 'ALL'];
            }
            case timestampOperators.weekInQuarter.key: {
              return ['1', '1', 'ALL'];
            }
            case timestampOperators.inYear.key: {
              return ['ALL'];
            }
          }
        }
        return this.getDefaultOperandArr(operatorKey, this.props.filterType);
      }
    }
    return [];
  }

  renderOperand() {
    const { id, operators } = this.props;
    const { operator, operands } = this.state;

    const currentOperator: IOperator = operators[operator];

    if (currentOperator?.requiresOperand && !currentOperator?.hideOperand) {
      let valueControl;
      // "Now()" or moment from value or instant moment if value is null
      let value =
        !isNil(operands) && operands.length > 0
          ? operands[0] === NOW
            ? operands[0]
            : moment(operands[0])
          : moment();

      switch (this.props.filterType) {
        case FilterTypes.BOOLEAN: {
          const current = join(operands as [], '');
          const boolOptions = map(['true', 'false'], boolName =>
            makeDropdownOption(boolName),
          );
          value = includes(map(boolOptions, 'value'), current.toLowerCase())
            ? current
            : '';

          valueControl = (
            <StyledSearchableDropdown
              id={`boolean-filter-dropdown`}
              onSelect={(options: ISearchableDropdownOption[]) => {
                const option = head(options);
                if (option) {
                  const { fieldDictionary = {} } = this.props;
                  const field = get(fieldDictionary, option?.value, {
                    name: option?.value,
                  });
                  this.onFieldOperandChange(field);
                }
              }}
              title={<span className={'dropdown-title'}>{value}</span>}
              options={boolOptions}
              defaultValue={makeDropdownOption(value)}
              popperSx={{ zIndex: 1050 }}
            />
          );
          break;
        }
        case FilterTypes.DATE: {
          const inTermOperator = this.isInTermOperator(
            this.props.condition.operator,
          );
          if (inTermOperator) {
            // Note: backend takes 1-indexed values of month/quarter operands
            const operandDefaults = this.getOperandDefaults(
              this.props.condition.operator,
            );

            const yearOperand = toString(
              last(operands) ?? last(operandDefaults),
            );

            const yearOperandLabel = convertYearOperandLabel(yearOperand);

            let currentYear = parseInt(moment().format('YYYY'));

            if (this.props.fiscalCalendarYearType === FiscalCalendarYear.END) {
              currentYear += 1;
            }

            const yearOperands = concat(
              makeDropdownOption('ALL', messages.filters.allYears),
              map(range(currentYear, currentYear - 10, -1), _yearIdx => {
                const yearLabel = convertYearOperandLabel(_yearIdx);
                return makeDropdownOption(`${_yearIdx}`, yearLabel);
              }),
            );

            const inTermOperands: {
              menuOptions: ISearchableDropdownOption[];
              defaultValue?: ISearchableDropdownOption;
              title: string;
              onSelect: (_value: string) => void;
            }[] = [];

            switch (inTermOperator.key) {
              case timestampOperators.inWeek.key: {
                const defaultWeekOperand = head(operandDefaults);
                const weekOperand: number = parseInt(
                  head(operands) ?? defaultWeekOperand,
                );
                const weekOperandLabel: string = toString(weekOperand);
                inTermOperands.push({
                  menuOptions: map(range(1, 53), _weekIdx =>
                    makeDropdownOption(`${_weekIdx}`),
                  ),
                  defaultValue: makeDropdownOption(
                    weekOperand,
                    weekOperandLabel,
                  ),
                  title: weekOperandLabel,
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(0, _operandValue),
                });

                inTermOperands.push({
                  menuOptions: yearOperands,
                  defaultValue: makeDropdownOption(
                    yearOperand,
                    yearOperandLabel,
                  ),
                  title: yearOperandLabel,
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(1, _operandValue),
                });
                break;
              }
              case timestampOperators.inMonth.key: {
                const defaultMonthOperand = head(operandDefaults);
                const monthOperand: number =
                  head(operands) ?? defaultMonthOperand;
                const monthOperandLabel: string = moment()
                  .month(monthOperand - 1)
                  .format('MMMM');
                inTermOperands.push({
                  menuOptions: map(range(1, 13), _monthIdx => {
                    const monthLabel = moment()
                      .month(_monthIdx - 1)
                      .format('MMMM');
                    return makeDropdownOption(`${_monthIdx}`, monthLabel);
                  }),
                  defaultValue: makeDropdownOption(
                    monthOperand,
                    monthOperandLabel,
                  ),
                  title: monthOperandLabel,
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(0, _operandValue),
                });

                inTermOperands.push({
                  menuOptions: yearOperands,
                  defaultValue: makeDropdownOption(
                    yearOperand,
                    yearOperandLabel,
                  ),
                  title: yearOperandLabel,
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(1, _operandValue),
                });
                break;
              }
              case timestampOperators.inQuarter.key: {
                const defaultQuarterOperand = head(operandDefaults);
                const quarterOperand =
                  (head(operands) as string) ?? defaultQuarterOperand;

                inTermOperands.push({
                  menuOptions: map(range(1, 5), _quarterIdx => {
                    const quarterValue = _quarterIdx;
                    return makeDropdownOption(`${quarterValue}`);
                  }),
                  defaultValue: makeDropdownOption(quarterOperand),
                  title: toString(quarterOperand),
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(0, _operandValue),
                });

                inTermOperands.push({
                  menuOptions: yearOperands,
                  defaultValue: makeDropdownOption(
                    yearOperand,
                    yearOperandLabel,
                  ),
                  title: yearOperandLabel,
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(1, _operandValue),
                });
                break;
              }
              case timestampOperators.weekInQuarter.key: {
                const quarterOperand =
                  (head(operands) as string) ?? head(operandDefaults);
                const weekOperand =
                  (nth(operands, 1) as string) ?? nth(operandDefaults, 1);
                //Quarter
                inTermOperands.push(
                  this.createOperandMenuItems(1, 5, quarterOperand, 0),
                );
                inTermOperands.push(
                  this.createOperandMenuItems(1, 14, weekOperand, 1),
                );
                inTermOperands.push({
                  menuOptions: yearOperands,
                  defaultValue: makeDropdownOption(
                    yearOperand,
                    yearOperandLabel,
                  ),
                  title: yearOperandLabel,
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(2, _operandValue),
                });
                break;
              }
              case timestampOperators.inYear.key: {
                inTermOperands.push({
                  menuOptions: yearOperands,
                  defaultValue: makeDropdownOption(
                    yearOperand,
                    yearOperandLabel,
                  ),
                  title: yearOperandLabel,
                  onSelect: _operandValue =>
                    this.onInTermOperandChange(0, _operandValue),
                });
                break;
              }
            }

            valueControl = map(
              inTermOperands,
              ({ menuOptions: _menuOptions, onSelect, title }, _idx) => {
                return (
                  <StyledSearchableDropdown
                    id={`${id}-interm-operand-dropdown-${_idx}`}
                    onSelect={(options: ISearchableDropdownOption[]) => {
                      const option = head(options);
                      if (isFunction(onSelect)) {
                        onSelect(option?.value);
                      }
                    }}
                    title={<span className={'dropdown-title'}>{title}</span>}
                    options={_menuOptions}
                    popperSx={{ zIndex: 1050 }}
                  />
                );
              },
            );
          } else {
            const disableTime =
              this.props.condition.operator &&
              operators[this.props.condition.operator]?.disableTime;

            let maybeNowOrDateString = value;

            if (isMoment(value)) {
              if (value.isValid()) {
                maybeNowOrDateString = value.format(
                  globalDateWithoutTimeFormat,
                );
              } else {
                maybeNowOrDateString = moment()
                  .startOf('day')
                  .format(globalDateWithoutTimeFormat);
              }
            }

            valueControl = (
              <DatePicker
                onChange={(selectedDate: ZonedDateTime) => {
                  this.onOperandChange(toIso8601(selectedDate));
                }}
                defaultValue={nowOrSpecifiedDate(maybeNowOrDateString)}
                showTime={!disableTime}
              />
            );
          }
          break;
        }
        default:
          valueControl = (
            <StyledFormControl
              key='numeric-condition-operand-key'
              type={'text'}
              onChange={this.onOperandChange}
              value={this.getOperandValue()}
            />
          );
      }
      if (currentOperator?.requiresFieldPicker) {
        const { fieldDictionary = {} } = this.props;
        const selected = get(fieldDictionary, this.state.operands[0], {
          name: this.state.operands[0] || '',
        } as IAnyAttribute);

        const fieldPicker = (
          <FieldsDropDown
            withoutField={this.props.field}
            selected={selected}
            dropDownPrefix={'filter-picker'}
            useAllFields={true}
            onSelect={newSelectedField => {
              this.onFieldOperandChange(newSelectedField);
            }}
          />
        );

        const parentLabel = (
          <span>
            {get(messages, 'filters.withParentField', 'with parent field')}
          </span>
        );

        valueControl = (
          <StyledFormControl
            key='string-condition-operand-key'
            type={'text'}
            onChange={event => {
              this.onFieldOperandValueChange(event);
            }}
            value={this.getFieldOperandValue()}
          />
        );

        const eqLabel = (
          <span>{get(messages, 'filters.eq', 'equal to').toLowerCase()}</span>
        );

        return [parentLabel, fieldPicker, eqLabel, valueControl];
      }
      return [valueControl];
    } else {
      return null;
    }
  }

  render() {
    const { id, operators, useFiscalCalendar, filterType } = this.props;
    const { operator } = this.state;
    const selectedOperator = operators[operator];
    let operatorLabel =
      useFiscalCalendar && !isNil(selectedOperator.fiscalDisplayText)
        ? selectedOperator.fiscalDisplayText
        : selectedOperator.displayText;
    if (selectedOperator.key === 'dynamicField') {
      operatorLabel = `filters.isCurrent${this.props.dynamicField?.sugarModule}`;
    }

    const operatorsArray = Object.values(operators);
    const filteredOperators =
      filterType === FilterTypes.DATE
        ? reject(operatorsArray, (op: IOperator) =>
            [
              timestampOperators.empty.key,
              timestampOperators.notEmpty.key,
            ].includes(op.key),
          )
        : operatorsArray;

    const operatorOptions = filteredOperators.map((op: IOperator) => {
      let _operatorOptionLabel =
        useFiscalCalendar && !isNil(op.fiscalDisplayText)
          ? op.fiscalDisplayText
          : op.displayText;
      if (op.key === 'dynamicField') {
        _operatorOptionLabel = `filters.isCurrent${this.props.dynamicField?.sugarModule}`;
      }
      return makeDropdownOption(
        op.key,
        get(messages, _operatorOptionLabel, _operatorOptionLabel),
      );
    });

    return (
      <FormGroup>
        <StyledSearchableDropdown
          id={`${id}-dropdown`}
          onSelect={(options: ISearchableDropdownOption[]) => {
            const option = head(options);

            if (option) {
              this.onOperatorChange(option?.value);
            }
          }}
          title={
            <span className={'dropdown-title'}>
              {get(messages, operatorLabel, operatorLabel)}
            </span>
          }
          options={operatorOptions}
          forceDisableSearch={this.props.isFilterAggregateDialog}
          popperSx={{ zIndex: 1050 }}
        />
        {this.renderOperand()}
      </FormGroup>
    );
  }
}

export const FilterCondition = compose<IFilterConditionProps, any>(
  connect((state: any, ownProps: any) => {
    const useFiscalCalendar = VIZ_SELECTORS.isFiscalCalendarActive(
      state,
      ownProps,
    );
    const fiscalCalendarYearType =
      VIZ_SELECTORS.getActiveDatasetFiscalYearSetting(state, ownProps);
    const dynamicField: DynamicFieldConfig = getDynamicFieldConfig(state);
    const { i18nPrefs = {} } = state?.account?.currentUser;

    const { id: chartType } = VIZ_SELECTORS.getActiveVizChartSpec(
      state,
      ownProps,
    );

    const discoveryId = VIZ_SELECTORS.getDiscoveryId(state, ownProps);
    const discovery = state.discover.openDiscoveries[discoveryId].present;
    const { viz } = discovery ?? {};

    const allFields = Viz.getAllAvailableFields(viz);
    const fieldDictionary = Viz.createFieldDictionary('name', allFields);

    return {
      chartType,
      dynamicField,
      useFiscalCalendar,
      fiscalCalendarYearType,
      i18nPrefs,
      isFilterAggregateDialog:
        state?.discover?.showFieldFilterDialog === FilterDialogTypes.AGGREGATE,
      fieldDictionary,
    };
  }),
  withProps((ownProps: any) => {
    let ops = FilterOperators.forFilterType(
      ownProps.filterType,
      ownProps.filterSubType,
    );

    if (
      isMatch(ownProps, {
        chartType: ChartSpecs.waterfall.id,
        filterType: FilterTypes.DATE,
      }) &&
      dateFilterSubTypeOrDefault(ownProps.filterSubType) ===
        TimestampFilterSubTypes.SET_CONDITION
    ) {
      ops = pick(
        timestampOperators,
        map(
          [
            timestampOperators.gt,
            timestampOperators.gte,
            timestampOperators.lt,
            timestampOperators.lte,
            timestampOperators.inWeek,
            timestampOperators.inMonth,
            timestampOperators.inQuarter,
            timestampOperators.weekInQuarter,
            timestampOperators.inYear,
          ],
          'key',
        ),
      );
    }

    if (!ownProps.dynamicField) {
      ops = omit(ops, 'dynamicField');
    }
    const first = Object.values(ops)[0]?.key;
    return {
      operators: ops,
      defaultSelected: first,
    };
  }),
)(UnconnectedFilterCondition as ComponentClass<IFilterConditionProps>);
